import { FileUpload } from '../models/file-upload';

/**
 * Which of the user's finished conversions belong to the tool page they are looking at?
 *
 * Why this matters: only 3.5% of chain events are tool switches
 * (docs/research/next-tool-transitions.md), so the overwhelmingly common continuation is
 * "run that again with a different setting" — which is exactly what Re-edit does. Re-edit
 * lives on `/downloads` and nowhere else, so the dominant next action costs a page change and
 * a hunt through every file in the session. See docs/proposed/RETENTION_FIXES.md T3.3.
 */

/** How many to offer. Enough to find the one they meant; not so many it becomes a file manager. */
export const RECENT_ON_PAGE_LIMIT = 3;

export interface PageIdentity {
    processType?: string | null;
    toType?: string | null;
    uniqueProcessType?: string | null;
}

function sameTool(f: FileUpload, page: PageIdentity): boolean {
    // uniqueProcessType is the most specific identifier a page has, so when both sides carry
    // one it decides on its own — two pages can share a processType/toType pair and still be
    // different tools (compress vs convert to the same format).
    const pageUnique = page.uniqueProcessType || null;
    const fileUnique = (f as any).uniqueProcessType || null;
    if (pageUnique && fileUnique) return pageUnique === fileUnique;

    const processMatches = !page.processType || `${(f as any).processType}` === `${page.processType}`;
    const toMatches = !page.toType || `${(f as any).toType}`.toLowerCase() === `${page.toType}`.toLowerCase();
    return processMatches && toMatches;
}

/**
 * Finished, non-failed conversions produced by this page, newest first.
 *
 * Failures are excluded deliberately: offering "re-edit" on a job that errored invites the user
 * to retry the same broken settings, and the error message on `/downloads` is where that
 * conversation belongs.
 */
export function recentOnThisPage(
    fileUploads: FileUpload[] | null | undefined,
    page: PageIdentity,
    limit = RECENT_ON_PAGE_LIMIT,
): FileUpload[] {
    if (!fileUploads || fileUploads.length === 0) return [];
    const done = fileUploads.filter(f => {
        try {
            if (!f || !f.result || (f as any).permanentlyDeleted) return false;
            if (f.result.error === true) return false;
            if (typeof f.isFailed === 'function' && f.isFailed()) return false;
            if (typeof f.isCompleted === 'function' && !f.isCompleted()) return false;
            return sameTool(f, page);
        } catch {
            return false;
        }
    });
    // Newest first. processEndTime is the honest ordering key; fall back to whatever order the
    // tracker gave us rather than dropping rows we cannot date.
    return done
        .slice()
        .sort((a, b) => time(b) - time(a))
        .slice(0, limit);
}

function time(f: FileUpload): number {
    const d = (f as any).processEndTime || (f as any).processStartTime || (f as any).uploadStartTime;
    const t = d instanceof Date ? d.getTime() : Date.parse(d || '');
    return Number.isFinite(t) ? t : 0;
}
